<script setup lang="ts">
import * as R from 'ramda'
import { onMounted, onUnmounted, useTemplateRef } from 'vue'

type Props = {
  /**
   * Defines the cropped, usable area of the pitch where incident coordinates can be clicked.
   *
   * If this value is `undefined`, the field will occupy the full width and height of the pitch.
   */
  fieldClass?: string
}

defineProps<Props>()

type Emits = {
  click: [
    {
      event: MouseEvent
      targetElement: HTMLElement
    },
  ]
}

const emit = defineEmits<Emits>()

// state
const field = useTemplateRef('field')

// methods
function logMouseEvent(event: MouseEvent): void {
  const props: (keyof MouseEvent)[] = [
    'type',
    'isTrusted',
    'clientX',
    'clientY',
    'button',
    'buttons',
    'ctrlKey',
    'shiftKey',
    'altKey',
    'metaKey',
    'timeStamp',
  ]

  const data = R.pick(props, event)

  // eslint-disable-next-line no-console
  console.debug('Field.vue logMouseEvent', JSON.stringify(data))
}

function clickHandler(event: MouseEvent): void {
  try {
    logMouseEvent(event)
  } finally {
    emit('click', { event, targetElement: field.value! })
  }
}

// init
onMounted(() => {
  field.value?.addEventListener('click', clickHandler)
  field.value?.addEventListener('contextmenu', clickHandler)
})

onUnmounted(() => {
  field.value?.removeEventListener('click', clickHandler)
  field.value?.removeEventListener('contextmenu', clickHandler)
})
</script>

<template>
  <div
    ref="field"
    class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
    :class="[
      {
        'h-full w-full': !fieldClass,
      },
      fieldClass,
    ]"
  >
    <slot />
  </div>
</template>
